/**
 * BulkReassignMentionsModal - Sprint 066
 *
 * Modal that reassigns a set of selected mentions from one business to another in a
 * single bulk operation. Reassign-only: the backend handler never modifies
 * `ReviewStatus` and never fires email notifications. See
 * `BulkReassignMentionsCommandHandler.cs` for the authoritative backend behavior.
 *
 * Pairs with `BusinessMentionsList` (selection state lives in the parent list and
 * flows down as `selectedMentions`).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Box,
  Text,
  Alert,
  Paper,
  ScrollArea,
  Textarea,
  Loader,
  Badge,
  UnstyledButton,
  Accordion,
  ThemeIcon
} from '@mantine/core';
import {
  IconSearch,
  IconBuilding,
  IconMapPin,
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconArrowsShuffle
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useBusinessSearch, type BusinessSearchResult } from '@/application/queries/useBusinessSearch';
import {
  mentionReviewKeys,
  useBulkReassignMentions
} from '@/application/queries/useMentionReview';
import { postReviewKeys } from '@/application/queries/usePostReview';
import type { MentionReviewDto } from '@/domain/entities/MentionReview';
import type { BulkReassignMentionError } from '@/infrastructure/api/MentionReviewApi';

// ============================================================================
// Types
// ============================================================================

export interface BulkReassignMentionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceBusinessId: string;
  sourceBusinessName: string;
  regionId?: string;
  selectedMentions: MentionReviewDto[];
  onSuccess: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const BulkReassignMentionsModal: React.FC<BulkReassignMentionsModalProps> = ({
  isOpen,
  onClose,
  sourceBusinessId,
  sourceBusinessName,
  regionId,
  selectedMentions,
  onSuccess
}): React.ReactElement => {
  // Target business search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessSearchResult | null>(null);

  // Admin notes + partial-failure error surface
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [apiErrors, setApiErrors] = useState<BulkReassignMentionError[]>([]);

  const queryClient = useQueryClient();
  const bulkReassignMutation = useBulkReassignMentions();
  const isSubmitting = bulkReassignMutation.isPending;

  // Region-scoped typeahead. `enabled` gates on the debounced query being non-empty
  // so typing doesn't fire a request on every keystroke.
  const { data: searchData, isLoading: isSearching } = useBusinessSearch(
    debouncedQuery,
    regionId,
    debouncedQuery.length >= 2
  );

  const mentionCount = selectedMentions.length;
  const pluralS = mentionCount === 1 ? '' : 's';
  const reviewedCount = selectedMentions.filter((m) => m.reviewStatus !== 'Pending').length;

  // Reset all local state when modal closes. The selection itself lives in the
  // parent, so we only clear what's owned here.
  const resetLocalState = useCallback((): void => {
    setSearchQuery('');
    setDebouncedQuery('');
    setSelectedBusiness(null);
    setAdminNotes('');
    setApiErrors([]);
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
  }, [debounceTimer]);

  // Clear transient state every time the modal re-opens so the user starts fresh
  // on each invocation.
  useEffect(() => {
    if (!isOpen) {
      resetLocalState();
    }
    // Intentionally depend only on `isOpen` to avoid re-running when the debounce
    // timer ref changes during normal typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = useCallback((): void => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const handleSearchChange = useCallback(
    (value: string): void => {
      setSearchQuery(value);
      setSelectedBusiness(null);

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      const timer = setTimeout(() => {
        setDebouncedQuery(value);
      }, 300);
      setDebounceTimer(timer);
    },
    [debounceTimer]
  );

  const handleConfirm = useCallback(async (): Promise<void> => {
    if (!selectedBusiness || mentionCount === 0) return;

    setApiErrors([]);

    try {
      const response = await bulkReassignMutation.mutateAsync({
        newBusinessId: selectedBusiness.id,
        sourceBusinessId,
        mentionIds: selectedMentions.map((m) => m.id),
        adminNotes: adminNotes.trim() || undefined
      });

      // Sprint 066: also invalidate post review caches — a reassigned mention may
      // appear inside a post in the Post Review list (mirrors ReassignBusinessModal).
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: mentionReviewKeys.details() });
      void queryClient.invalidateQueries({ queryKey: postReviewKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: postReviewKeys.details() });

      if (response.failedCount === 0) {
        onSuccess();
      } else {
        // Partial failure — keep modal open and show per-item errors so the admin
        // can see which mentions didn't move.
        setApiErrors(response.errors);
      }
    } catch {
      // `useBulkReassignMentions` already surfaces a toast via its `onError`.
      // Modal stays open so the admin can retry.
    }
  }, [
    selectedBusiness,
    mentionCount,
    selectedMentions,
    sourceBusinessId,
    adminNotes,
    bulkReassignMutation,
    queryClient,
    onSuccess
  ]);

  const canSubmit = selectedBusiness !== null && mentionCount > 0 && !isSubmitting;

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconArrowsShuffle size={20} />
          <Text fw={600}>
            Reassign {mentionCount} mention{pluralS} from {sourceBusinessName}
          </Text>
        </Group>
      }
      size="lg"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <Stack gap="md">
        {/* Source business context */}
        <Alert icon={<IconBuilding size={16} />} color="blue" variant="light">
          <Text size="sm">
            Reassigning from: <Text span fw={600}>{sourceBusinessName}</Text>
          </Text>
        </Alert>

        {/* Reviewed mentions warning */}
        {reviewedCount > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="yellow"
            variant="light"
            title="Some selected mentions are already reviewed"
          >
            <Text size="sm">
              {reviewedCount} selected mention{reviewedCount === 1 ? ' has' : 's have'} already
              been reviewed. Reassignment will be logged but their Approved/Rejected status will
              not change.
            </Text>
          </Alert>
        )}

        <ScrollArea.Autosize mah={420} offsetScrollbars>
          <Stack gap="sm">
            {/* Target business typeahead */}
            <TextInput
              label="Target business"
              description="Search businesses in the same region as the source"
              placeholder="Search businesses by name..."
              leftSection={<IconSearch size={16} />}
              rightSection={isSearching ? <Loader size={16} /> : null}
              value={searchQuery}
              onChange={(e): void => handleSearchChange(e.currentTarget.value)}
              disabled={isSubmitting}
              autoFocus
            />

            {/* Search results */}
            {searchData?.results && searchData.results.length > 0 && !selectedBusiness && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed">
                  {searchData.totalCount} result{searchData.totalCount !== 1 ? 's' : ''} found
                </Text>
                {searchData.results
                  .filter((business) => business.id !== sourceBusinessId)
                  .map((business) => (
                    <UnstyledButton
                      key={business.id}
                      onClick={(): void => setSelectedBusiness(business)}
                    >
                      <Paper
                        p="sm"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 150ms ease'
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Box style={{ minWidth: 0 }}>
                            <Text fw={600} size="sm" truncate>
                              {business.displayName || business.name}
                            </Text>
                            {business.formattedAddress && (
                              <Text size="xs" c="dimmed" truncate>
                                <IconMapPin
                                  size={12}
                                  style={{ verticalAlign: 'middle', marginRight: 4 }}
                                />
                                {business.formattedAddress}
                              </Text>
                            )}
                          </Box>
                          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                            {business.companyName && (
                              <Badge size="xs" variant="light" color="violet">
                                {business.companyName}
                              </Badge>
                            )}
                            {business.category && (
                              <Badge size="xs" variant="light" color="gray">
                                {business.category}
                              </Badge>
                            )}
                          </Group>
                        </Group>
                      </Paper>
                    </UnstyledButton>
                  ))}
              </Stack>
            )}

            {/* No results */}
            {debouncedQuery.length >= 2 &&
              !isSearching &&
              searchData?.results?.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No businesses found. Try a different search.
                </Text>
              )}

            {/* Selected target confirmation */}
            {selectedBusiness && (
              <Paper
                p="md"
                withBorder
                style={{
                  backgroundColor: 'var(--mantine-color-green-0)',
                  borderColor: 'var(--mantine-color-green-4)'
                }}
              >
                <Group justify="space-between" align="flex-start">
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                      Reassign to
                    </Text>
                    <Text fw={600}>
                      {selectedBusiness.displayName || selectedBusiness.name}
                    </Text>
                    {selectedBusiness.formattedAddress && (
                      <Text size="sm" c="dimmed">
                        {selectedBusiness.formattedAddress}
                      </Text>
                    )}
                    {selectedBusiness.companyName && (
                      <Badge size="xs" variant="light" color="violet" mt={4}>
                        {selectedBusiness.companyName}
                      </Badge>
                    )}
                  </Box>
                  <Button
                    variant="subtle"
                    color="gray"
                    size="xs"
                    onClick={(): void => setSelectedBusiness(null)}
                    disabled={isSubmitting}
                  >
                    Change
                  </Button>
                </Group>
              </Paper>
            )}

            {/* Admin notes */}
            <Textarea
              label="Admin notes (optional)"
              description="Applied to every reassigned mention in this batch"
              placeholder="Reason for the bulk reassignment..."
              value={adminNotes}
              onChange={(e): void => setAdminNotes(e.currentTarget.value)}
              minRows={2}
              disabled={isSubmitting}
            />

            {/* Per-item errors (partial failure) */}
            {apiErrors.length > 0 && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                title={`${apiErrors.length} mention${apiErrors.length === 1 ? '' : 's'} failed to reassign`}
              >
                <Accordion variant="separated" defaultValue="errors">
                  <Accordion.Item value="errors">
                    <Accordion.Control>
                      <Group gap="xs">
                        <ThemeIcon color="red" size="sm" variant="light">
                          <IconAlertCircle size={14} />
                        </ThemeIcon>
                        <Text size="sm">Show error details</Text>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        {apiErrors.map((err) => (
                          <Text key={err.mentionId} size="xs" c="dimmed">
                            <Text span fw={600} c="dark">
                              {err.mentionId.slice(0, 8)}…
                            </Text>
                            {' — '}
                            {err.reason}
                          </Text>
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Alert>
            )}
          </Stack>
        </ScrollArea.Autosize>

        {/* Footer */}
        <Box
          pt="sm"
          style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
        >
          <Group gap="sm" justify="flex-end">
            <Button
              variant="subtle"
              color="gray"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              color="blue"
              leftSection={<IconCheck size={16} />}
              onClick={(): void => {
                void handleConfirm();
              }}
              loading={isSubmitting}
              disabled={!canSubmit}
            >
              Reassign {mentionCount} mention{pluralS}
            </Button>
          </Group>
        </Box>
      </Stack>
    </Modal>
  );
};
